import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

import { api, formatApiError } from "@/lib/api";
import ReportPageShell from "@/components/reports/ReportPageShell";
import DateRangePicker, { rangeToParams } from "@/components/reports/DateRangePicker";
import ExportDropdown from "@/components/reports/ExportDropdown";
import EngineerMultiSelect, { engineerFilterToParam } from "@/components/reports/EngineerMultiSelect";
import StatusBadge from "@/components/StatusBadge";

const CHART_COLOR = "hsl(var(--primary))";

export default function EngineersReport() {
  const [range, setRange] = useState({ preset: "30d", from: null, to: null });
  const [engineerFilter, setEngineerFilter] = useState(new Set()); // empty = all
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const my = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { ...rangeToParams(range) };
      const eid = engineerFilterToParam(engineerFilter);
      if (eid) params.engineer_id = eid;
      const { data } = await api.get("/reports/engineers", { params });
      if (my !== reqIdRef.current) return;
      setItems(data.items || []);
    } catch (err) {
      if (my !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (my === reqIdRef.current) setLoading(false);
    }
  }, [range, engineerFilter]);

  useEffect(() => { load(); }, [load]);

  const exportParams = { ...rangeToParams(range) };
  const eid = engineerFilterToParam(engineerFilter);
  if (eid) exportParams.engineer_id = eid;

  const chart = (
    <div style={{ width: "100%", height: Math.max(240, items.length * 32 + 40) }}>
      <ResponsiveContainer>
        <BarChart data={items} layout="vertical" margin={{ left: 60, right: 20, top: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
          <YAxis type="category" dataKey="engineer_name" stroke="hsl(var(--muted-foreground))" fontSize={12} width={140} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
          <Bar dataKey="jobs_total" fill={CHART_COLOR} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <ReportPageShell
      testId="engineers-report"
      title="Per-Engineer Jobs Report"
      subtitle="Job counts, time on site, and actions-required rate."
      headerActions={
        <ExportDropdown
          basePath="/reports/engineers"
          params={exportParams}
          filename="engineers-report"
          testId="engineers-report-export"
        />
      }
      filters={
        <>
          <DateRangePicker value={range} onChange={setRange} testId="engineers-report-range" />
          <EngineerMultiSelect value={engineerFilter} onChange={setEngineerFilter} testId="engineers-report-eng" />
        </>
      }
      chartTitle="Jobs total per engineer"
      chart={chart}
      table={
        <table className="w-full text-sm" data-testid="engineers-report-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-2 px-3">Engineer</th>
              <th className="text-right py-2 px-3">Jobs</th>
              <th className="text-right py-2 px-3">Completed</th>
              <th className="text-right py-2 px-3">Closed</th>
              <th className="text-right py-2 px-3">Aborted</th>
              <th className="text-right py-2 px-3">Avg time (min)</th>
              <th className="text-right py-2 px-3">Total time (h)</th>
              <th className="text-right py-2 px-3">Actions rate</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="py-8 text-center text-xs text-muted-foreground">No data in range.</td></tr>
            ) : items.map((r) => (
              <tr key={r.engineer_id} data-testid={`engineers-report-row-${r.engineer_id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="py-2 px-3 font-medium">{r.engineer_name}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.jobs_total}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.by_status.completed}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.by_status.closed}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.by_status.aborted}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.avg_time_on_site_minutes}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.total_time_on_site_hours}</td>
                <td className="py-2 px-3 text-right">
                  <StatusBadge tone={r.actions_flagged_rate_pct > 25 ? "warning" : "muted"}>
                    {r.actions_flagged_rate_pct}%
                  </StatusBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    />
  );
}
